// @flow

import * as React from 'react';
import Glyphicon from 'react-bootstrap/lib/Glyphicon';

import ButtonAction from './ButtonAction';

import type { CommonActionProps } from './types.actions';
import { DesktopView, MobileView } from '../../config/mediaQuery';

type GridRow = {
  isCollapsed: boolean,
  level?: number,
  hasChildren?: boolean,
};

type Grid = {
  rows: Array<GridRow>,
  collapseGroupsToLevel: (level: number) => void,
};

type Props = {
  grid: Grid,
  disabled?: boolean,
  onGroupCollapsedChanged: () => void,
  setCollapseLoading?: (isLoading: boolean) => void,
};

type State = {
  isExpanding: boolean,
};

class ExpandAction extends React.Component<Props & CommonActionProps, State> {
  static defaultProps = {
    size: 'xsmall',
    disabled: false,
  };

  state = {
    isExpanding: false,
  };

  handleExpand = () => {
    const { grid, onGroupCollapsedChanged, setCollapseLoading } = this.props;

    // Check if any rows are actually collapsed
    const hasCollapsedRows = grid.rows.some(row => row.isCollapsed === true);
    if (!hasCollapsedRows) return;

    // Show overlay and disable button before blocking operation
    this.setState({ isExpanding: true });
    if (setCollapseLoading) {
      setCollapseLoading(true);
    }

    // Use requestAnimationFrame + setTimeout to ensure the browser has time to repaint
    // and show the BlockingStatus overlay before the blocking operation begins
    requestAnimationFrame(() => {
      setTimeout(() => {
        // Use collapseGroupsToLevel(100) as a single native Wijmo operation.
        // This both expands all rows AND resets Wijmo's internal _collapseLevel.
        // Without this reset, _collapseLevel stays at 0 (from CollapseAction),
        // causing new GroupRows created during filter changes to re-collapse.
        //
        // This is much faster than batched row iteration because:
        // 1. Single internal refresh instead of N/BATCH_SIZE refreshes
        // 2. No per-batch collectionView.beginUpdate/endUpdate overhead
        // 3. No redundant onGroupCollapsedChanged calls per batch
        grid.collapseGroupsToLevel(100);
        onGroupCollapsedChanged();

        // Hide overlay after the browser has painted the new grid state
        // Use requestAnimationFrame to ensure the grid's visual update is complete
        this.setState({ isExpanding: false });
        if (setCollapseLoading) {
          requestAnimationFrame(() => {
            setCollapseLoading(false);
          });
        }
      }, 50);
    });
  };

  render() {
    const { size, disabled } = this.props;
    const { isExpanding } = this.state;

    return (
      <>
        <DesktopView>
          <ButtonAction
            onClick={this.handleExpand}
            bsSize={size}
            disabled={disabled || isExpanding}
          >
            <Glyphicon glyph="collapse-down" /> {isExpanding ? 'Expanding...' : 'Expand'}
          </ButtonAction>
        </DesktopView>
        <MobileView>
          <button
            className='collapse-menu-button'
            onClick={this.handleExpand}
            disabled={disabled || isExpanding}
          >
            <img src='assets/expand.svg' alt='expand icon'/> {isExpanding ? 'Expanding...' : 'Expand'}
          </button>
        </MobileView>
      </>
    );
  }
}

export default ExpandAction;
